import {
  Component,
  ChangeDetectionStrategy,
  inject,
  input,
  output,
  signal,
  computed,
  effect,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RankingService } from '../../services';
import {
  RankingRecommendationEntity,
  PositioningHint,
  mapRecommendationsToHints,
  annotateHintLocks,
} from '../../models';
import { EntitlementsStore } from '@modules/entitlements/services';
import { UpgradeService } from '@shared/services/upgrade.service';
import { PositioningCardComponent } from '../positioning-card/positioning-card.component';

/**
 * ──────────────────────────────────────────────────────────────
 *  WIDŻET POZYCJONOWANIA OBIEKTU (smart)
 * ──────────────────────────────────────────────────────────────
 *
 *  Ładuje BEZPŁATNE rekomendacje pozycjonowania dla pojedynczego obiektu
 *  i renderuje pełną kartę. Emituje (navigate) gdy użytkownik kliknie
 *  wskazówkę — rodzic (np. kreator) decyduje dokąd przejść.
 *
 *  Pobiera dane tylko gdy jest faktycznie wyrenderowany.
 */
@Component({
  selector: 'app-object-positioning-widget',
  standalone: true,
  imports: [PositioningCardComponent],
  templateUrl: './object-positioning-widget.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ObjectPositioningWidgetComponent {
  private readonly rankingService = inject(RankingService);
  private readonly entitlements = inject(EntitlementsStore);
  private readonly upgradeService = inject(UpgradeService);
  private readonly destroyRef = inject(DestroyRef);

  readonly objectId = input.required<number>();

  /** Klik wskazówki — rodzic obsługuje nawigację (zna kontekst). */
  readonly navigate = output<PositioningHint>();

  readonly loading = signal(false);
  readonly errored = signal(false);
  readonly ranking = signal<RankingRecommendationEntity | null>(null);

  /** Reaktywne entitlements — pozwala przeliczyć locki po doładowaniu planu. */
  private readonly plan = this.entitlements.plan;

  readonly completionPercent = computed(() => this.ranking()?.completionPercent ?? null);
  readonly hints = computed<PositioningHint[]>(() => {
    this.plan(); // zależność: przelicz locki gdy plan się zmieni / doładuje
    const mapped = mapRecommendationsToHints(this.ranking()?.recommendations ?? []);
    return annotateHintLocks(mapped, (feature) => this.entitlements.hasFeature(feature));
  });

  /** CTA "Podbij plan" na zablokowanej wskazówce → strona planów. */
  onUpgrade(): void {
    this.upgradeService.goToPlans('positioning');
  }

  constructor() {
    effect(() => {
      const id = this.objectId();
      if (id && id > 0) {
        this.load(id);
      }
    });
  }

  private load(objectId: number): void {
    this.loading.set(true);
    this.errored.set(false);

    this.rankingService
      .getRankingRecommendations(objectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (entity) => {
          this.ranking.set(entity);
          this.loading.set(false);
        },
        error: () => {
          this.ranking.set(null);
          this.errored.set(true);
          this.loading.set(false);
        },
      });
  }
}
